import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Badge,
  Avatar,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Select,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import axios from 'axios';

const CreatorEarnings = () => {
  const [creators, setCreators] = useState([]);
  const [filteredCreators, setFilteredCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('earnings');
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchCreatorsData();
  }, []);

  useEffect(() => {
    filterAndSortCreators();
  }, [creators, searchTerm, sortBy]);

  const fetchCreatorsData = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) throw new Error('Unauthorized');
      const apiBaseUrl = (
        process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_BASE_URL ||
        window.location.origin ||
        'http://localhost:3030'
      )
        .trim()
        .replace(/\/$/, '');

      // Fetch all users who are creators (have earnings > 0)
      const response = await axios.get(`${apiBaseUrl}/api/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        // Include anyone who is marked creator OR has earnings > 0
        const creatorsData = response.data.data.filter(
          (user) => user.isCreator || user.earnings > 0,
        );
        setCreators(creatorsData);
      } else {
        setError('Failed to fetch creators data');
      }
    } catch (err) {
      console.error('Error fetching creators:', err);
      setError(err.response?.data?.message || 'Failed to fetch creators data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (creator) => {
    setSelectedCreator(creator);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCreator(null);
  };

  const filterAndSortCreators = () => {
    let filtered = creators.filter(
      (creator) =>
        creator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        creator.email.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    // Sort by selected criteria
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'earnings':
          return b.earnings - a.earnings;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'stories':
          return (b.storiesCount || 0) - (a.storiesCount || 0);
        default:
          return 0;
      }
    });

    setFilteredCreators(filtered);
  };

  const formatCurrency = (amount) => {
    return `${amount} 💎`;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="400px"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertTitle>Error!</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box p={6}>
      <Heading mb={6} size="lg">
        Creator Earnings Management
      </Heading>

      {/* Summary Cards */}
      <Flex gap={4} mb={6} wrap="wrap">
        <Card
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          shadow="md"
          flex={1}
          minW="200px"
        >
          <CardBody>
            <Text fontSize="sm" color="gray.600">
              Total Creators
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.500">
              {creators.length}
            </Text>
          </CardBody>
        </Card>
        <Card
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          shadow="md"
          flex={1}
          minW="200px"
        >
          <CardBody>
            <Text fontSize="sm" color="gray.600">
              Total Earnings Paid
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.500">
              {formatCurrency(
                creators.reduce((sum, creator) => sum + creator.earnings, 0),
              )}
            </Text>
          </CardBody>
        </Card>
        <Card
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          shadow="md"
          flex={1}
          minW="200px"
        >
          <CardBody>
            <Text fontSize="sm" color="gray.600">
              Average Earnings
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="purple.500">
              {creators.length > 0
                ? formatCurrency(
                    (
                      creators.reduce(
                        (sum, creator) => sum + creator.earnings,
                        0,
                      ) / creators.length
                    ).toFixed(1),
                  )
                : '0 💎'}
            </Text>
          </CardBody>
        </Card>
      </Flex>

      {/* Filters */}
      <Card
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        shadow="md"
        mb={6}
      >
        <CardBody>
          <HStack spacing={4} wrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search creators..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              maxW="200px"
            >
              <option value="earnings">Sort by Earnings</option>
              <option value="name">Sort by Name</option>
              <option value="stories">Sort by Stories</option>
            </Select>
          </HStack>
        </CardBody>
      </Card>

      {/* Creators Table */}
      <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
        <CardHeader>
          <Heading size="md">Creators & Their Earnings</Heading>
        </CardHeader>
        <CardBody>
          {filteredCreators.length === 0 ? (
            <Text textAlign="center" py={8} color="gray.500">
              No creators found with earnings.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Creator</Th>
                    <Th>Email</Th>
                    <Th>Total Earnings</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredCreators.map((creator) => (
                    <Tr key={creator._id}>
                      <Td>
                        <HStack>
                          <Avatar
                            size="sm"
                            name={creator.name}
                            src={creator.profileImage}
                          />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">{creator.name}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {creator.followersCount || 0} followers
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>{creator.email}</Td>
                      <Td>
                        <Badge colorScheme="green" fontSize="md" p={2}>
                          {formatCurrency(creator.earnings)}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            creator.status === 'active' ? 'green' : 'red'
                          }
                        >
                          {creator.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => openModal(creator)}
                        >
                          View Details
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
      {/* Detail Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Creator Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedCreator ? (
              <>
                <HStack spacing={4} align="center" mb={4}>
                  <Avatar
                    size="lg"
                    name={selectedCreator.name}
                    src={selectedCreator.profileImage}
                  />
                  <Box>
                    <Text fontWeight="bold" fontSize="lg">
                      {selectedCreator.name}
                    </Text>
                    <Text color="gray.600">{selectedCreator.email}</Text>
                    <Text color="gray.500">
                      Followers: {selectedCreator.followersCount || 0}
                    </Text>
                  </Box>
                </HStack>
                <Divider mb={4} />
                <VStack align="start" spacing={2}>
                  <Text>
                    <strong>Total Earnings:</strong>{' '}
                    {formatCurrency(selectedCreator.earnings)}
                  </Text>
                  <Text>
                    <strong>Status:</strong> {selectedCreator.status}
                  </Text>
                  <Text>
                    <strong>Stories:</strong> {selectedCreator.storiesCount || 0}
                  </Text>
                  {selectedCreator.subscriptionStatus && (
                    <Text>
                      <strong>Subscription:</strong>{' '}
                      {selectedCreator.subscriptionStatus}
                    </Text>
                  )}
                  <Text>
                    <strong>Joined:</strong>{' '}
                    {new Date(selectedCreator.createdAt).toLocaleString()}
                  </Text>
                </VStack>
              </>
            ) : (
              <Text>Loading...</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeModal}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CreatorEarnings;
